"use client";

import type { ReactNode } from "react";
import { useSubmitShortcut } from "@/lib/hooks/useSubmitShortcut";
import { cn } from "./ui/cn";

function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="inline-block rounded border border-border-strong bg-surface px-1 font-sans text-[10px] leading-[15px] text-muted-foreground">
      {children}
    </kbd>
  );
}

/**
 * A muted line under an inline composer explaining how to send.
 *
 * Two triggers, answering two different questions.
 *
 * On focus it *teaches* — a standing offer to anyone who has not used the
 * shortcut yet, revealed in pure CSS (so the parent form must carry `group`)
 * and retired once the counter says they have learned it.
 *
 * Once `value` carries a newline it *answers*: the person just pressed the key
 * that sends a message everywhere else and got a linebreak instead, and is owed
 * the reason right then. That is the trigger the pre-port composer had and the
 * one people remember (OSO-525), and it is deliberately not subject to the
 * counter — knowing the shortcut months ago does not help at the moment Enter
 * behaves unexpectedly. It stays rare on its own: most inline comments never
 * contain a linebreak at all.
 *
 * Renders nothing on touch-only devices, which have no modifier key to press.
 */
export function SubmitHint({ value }: { value?: string }) {
  const { modKey, showHint } = useSubmitShortcut();
  const brokeLine = value?.includes("\n") ?? false;
  if (modKey === null || (!showHint && !brokeLine)) return null;

  return (
    <p
      aria-hidden="true"
      className={cn(
        "overflow-hidden transition-all duration-200 text-[11px] text-subtle-foreground pl-4 pt-1",
        brokeLine
          ? "max-h-6 opacity-100"
          : "max-h-0 opacity-0 group-focus-within:max-h-6 group-focus-within:opacity-100"
      )}
    >
      <Kbd>{modKey}</Kbd> + <Kbd>Enter</Kbd> sender · <Kbd>Enter</Kbd> laver linjeskift
    </p>
  );
}

/**
 * Wraps a submit control in a tooltip naming the action and its shortcut.
 * Shows on hover and on keyboard focus; hidden from assistive tech, which
 * reads the control's own label instead.
 *
 * `className` MUST establish a positioning context for the tooltip — either
 * `relative`, or the `absolute …` coordinates the wrapped control used to carry
 * itself. Don't default it to `relative` here: Tailwind emits `relative` after
 * `absolute`, so a hardcoded one silently beats whatever the caller passes and
 * drops the control back into normal flow.
 */
export function SubmitTooltip({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className: string;
}) {
  const { modKey } = useSubmitShortcut();

  return (
    <span className={`group/tip inline-flex ${className}`}>
      {children}
      {/* Purely visual — assistive tech reads the control's own label instead. */}
      <span
        data-testid="submit-tooltip"
        aria-hidden="true"
        className="pointer-events-none absolute bottom-full right-0 mb-1.5 z-20 whitespace-nowrap rounded-md bg-gray-900 dark:bg-gray-700 px-2 py-1 text-[11px] font-normal text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover/tip:opacity-100 group-focus-within/tip:opacity-100"
      >
        {label}
        {modKey && <span className="ml-1.5 text-subtle-foreground">{modKey} + Enter</span>}
      </span>
    </span>
  );
}
